import { Auth0ContextInterface } from "@auth0/auth0-react";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { Action } from "redux";
import type { AppThunk } from "./modelActionCreators";
import type { RootState } from ".";
import { DependencyStatus, KeyStatusCounts, StatusCountsRequest, StatusCountsResponse } from "./types";

const cozonacApiUrl = process.env.REACT_APP_COZONAC_API_URL;

/** Keys loaded (or failed) less than this long ago are not requested again. */
export const StatusCountsTtlMs = 60_000;
/** Pending keys are sent this long after the last block load adds to them. */
export const StatusCountsDebounceMs = 250;
/** Continuous block loads can push the debounce back for at most this long. */
export const StatusCountsMaxWaitMs = 1_000;
/** Matches the API's per-request key cap. */
export const StatusCountsChunkSize = 100;

export type StatusCountsEntry =
  | { state: "loading" }
  | { state: "loaded"; data: KeyStatusCounts; asOf: string; fetchedAt: number }
  | { state: "error"; fetchedAt: number };

export interface StatusCountsState {
  byKey: Record<string, StatusCountsEntry>;
  /** Completed-row retention from the last response; null until one arrives. */
  retentionDays: number | null;
  /** Bumped by clear() so responses to requests sent before a purge are dropped. */
  generation: number;
}

export const initialStatusCountsState: StatusCountsState = {
  byKey: {},
  retentionDays: null,
  generation: 0,
};

const slice = createSlice({
  name: "statusCounts",
  initialState: initialStatusCountsState,
  reducers: {
    markLoading(state, action: PayloadAction<string[]>) {
      for (const key of action.payload) {
        state.byKey[key] = { state: "loading" };
      }
    },
    received(
      state,
      action: PayloadAction<{ keys: string[]; response: StatusCountsResponse; generation: number; fetchedAt: number }>
    ) {
      const { keys, response, generation, fetchedAt } = action.payload;
      if (generation !== state.generation) return;

      state.retentionDays = response.completedRetentionDays;
      for (const entry of response.keys ?? []) {
        state.byKey[entry.partitionKey] = { state: "loaded", data: entry, asOf: response.asOf, fetchedAt };
      }

      // The API zero-fills every requested key; anything missing is treated as a failure
      // so it is not left loading.
      for (const key of keys) {
        if (state.byKey[key]?.state === "loading") {
          state.byKey[key] = { state: "error", fetchedAt };
        }
      }
    },
    failed(state, action: PayloadAction<{ keys: string[]; generation: number; fetchedAt: number }>) {
      const { keys, generation, fetchedAt } = action.payload;
      if (generation !== state.generation) return;

      for (const key of keys) {
        state.byKey[key] = { state: "error", fetchedAt };
      }
    },
    clear(state) {
      state.byKey = {};
      state.generation += 1;
    },
  },
});

const ProgressStatuses = [DependencyStatus.Waiting, DependencyStatus.Notified, DependencyStatus.Failed];

/** True for the statuses that get a progress marker and tooltip: Waiting, Notified, Failed (name or number). */
export const isProgressStatus = (status: unknown): boolean =>
  ProgressStatuses.some((s) => status === s || status === DependencyStatus[s]);

type GridRow = { id?: string; partitionKey?: string; status?: unknown };

/** A leaf row (it has an id; group rows do not) in a progress status: the rows that are decorated. */
export const isProgressRow = (row: GridRow | undefined): boolean => !!row?.id && isProgressStatus(row.status);

/** Distinct keys of the progress rows. */
export const statusCountKeysForRows = (rows: readonly GridRow[]): string[] => [
  ...new Set(rows.filter((r) => isProgressRow(r) && r.partitionKey).map((r) => r.partitionKey as string)),
];

const needsFetch = (entry: StatusCountsEntry | undefined, now: number): boolean =>
  !entry || (entry.state !== "loading" && now - entry.fetchedAt >= StatusCountsTtlMs);

// The batcher: keys from block loads that land within the debounce window go out together.
let pendingKeys = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let firstPendingAt: number | null = null;

/** Test hook: drops pending keys and any scheduled flush. */
export const resetStatusCountsBatcher = (): void => {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  firstPendingAt = null;
  pendingKeys = new Set<string>();
};

const fetchChunk = async (
  dispatch: (action: Action<string>) => void,
  auth0Context: Auth0ContextInterface,
  keys: string[],
  generation: number
): Promise<void> => {
  try {
    const token = await auth0Context.getAccessTokenSilently();
    const body: StatusCountsRequest = { partitionKeys: keys };
    const resp = await axios.post<StatusCountsResponse>(`${cozonacApiUrl}/api/dependencies/statusCounts`, body, {
      headers: { Authorization: `bearer ${token}` },
    });
    dispatch(slice.actions.received({ keys, response: resp.data, generation, fetchedAt: Date.now() }));
  } catch (err) {
    // A failed decoration must not raise an alert: no snackbar, no grid error, no marker.
    console.warn(`Status counts request failed for ${keys.length} keys`, err);
    dispatch(slice.actions.failed({ keys, generation, fetchedAt: Date.now() }));
  }
};

/**
 * Queues the keys of the given grid rows for a status-count request. Keys that are loading,
 * or were fetched within the TTL, are skipped. Never awaits the request.
 */
const requestForRows =
  (auth0Context: Auth0ContextInterface, rows: readonly GridRow[]): AppThunk =>
  (dispatch, getState) => {
    const now = Date.now();
    const { byKey } = getState().statusCounts;
    const keys = statusCountKeysForRows(rows).filter((key) => needsFetch(byKey[key], now));

    if (keys.length === 0) return;

    dispatch(slice.actions.markLoading(keys));
    keys.forEach((key) => pendingKeys.add(key));

    // Debounce from the last enqueue, but never later than the max wait after the first.
    firstPendingAt ??= now;
    const delay = Math.min(StatusCountsDebounceMs, Math.max(0, firstPendingAt + StatusCountsMaxWaitMs - now));

    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      flushTimer = null;
      firstPendingAt = null;
      const batch = [...pendingKeys];
      pendingKeys = new Set<string>();
      const { generation } = getState().statusCounts;

      for (let i = 0; i < batch.length; i += StatusCountsChunkSize) {
        void fetchChunk(dispatch, auth0Context, batch.slice(i, i + StatusCountsChunkSize), generation);
      }
    }, delay);
  };

export const statusCountsActions = { ...slice.actions, requestForRows };

export const selectStatusCountsEntry =
  (partitionKey: string | undefined) =>
  (state: RootState): StatusCountsEntry | undefined =>
    partitionKey ? state.statusCounts?.byKey[partitionKey] : undefined;

export const selectRetentionDays = (state: RootState): number | null => state.statusCounts?.retentionDays ?? null;

export default slice.reducer;
